import Assignment from '@mui/icons-material/AssignmentOutlined'
import CalendarMonth from '@mui/icons-material/CalendarMonthOutlined'
import Campaign from '@mui/icons-material/CampaignOutlined'
import Chat from '@mui/icons-material/ChatBubbleOutlineOutlined'
import Check from '@mui/icons-material/Check'
import Dashboard from '@mui/icons-material/DashboardOutlined'
import EventAvailable from '@mui/icons-material/EventAvailableOutlined'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Groups from '@mui/icons-material/GroupsOutlined'
import BarChart from '@mui/icons-material/InsertChartOutlined'
import MoreHoriz from '@mui/icons-material/MoreHoriz'
import Settings from '@mui/icons-material/SettingsOutlined'
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  ButtonBase,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { type ComponentChildren, type ComponentType, createContext } from 'preact'
import { useContext, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import type { EventSummary } from '../api/generated/EventSummary'
import { useApi } from '../api/useApi'
import { AccountMenu } from '../auth/AccountMenu'
import { eventDates } from '../organizer/overview'
import { useEvent } from './EventContext'
import { type Section, sectionOf, sectionsOf } from './sections'
import { summaryDays } from './summaries'

const DRAWER_WIDTH = 260
const TAB_BAR_HEIGHT = 64

const icons: Record<string, ComponentType> = {
  '/': Dashboard,
  '/planning': CalendarMonth,
  '/volunteers': Groups,
  '/messages': Chat,
  '/announcements': Campaign,
  '/positions': Assignment,
  '/stats': BarChart,
  '/settings': Settings,
  '/availability': EventAvailable,
}

const SectionIcon = ({ section }: { section: Section }) => {
  const Icon = icons[section.path] ?? Dashboard
  return <Icon />
}

export type Badges = Partial<Record<NonNullable<Section['badge']>, number>>

/** `dates` replaces the event's dates of the user's event list, when the space knows newer ones. */
type Shell = { badges: Badges; dates?: string }

const ShellContext = createContext<Shell>({ badges: {} })

const badgeColors = { pending: 'warning', unread: 'primary' } as const

/** The sections of the user's space, the current one and their paths. */
function useNavigation() {
  const { event } = useEvent()
  const { path } = useLocation()
  const base = `/events/${event.slug}`
  const sections = sectionsOf(event.role)
  const current = sectionOf(sections, path.slice(base.length))
  const href = (section: Section) => (section.path === '/' ? base : `${base}${section.path}`)
  return { sections, current, href }
}

/**
 * Name and dates of the event, opening a menu back to all the user's events, or to the same
 * section of another event where they have the same role.
 */
function EventSwitcher({ compact = false }: { compact?: boolean }) {
  const { event } = useEvent()
  const shell = useContext(ShellContext)
  const { current } = useNavigation()
  const events = useApi<EventSummary[]>('/events')
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const sameRole = events.data?.filter((e) => e.role === event.role) ?? []
  const summary = events.data?.find((e) => e.slug === event.slug)
  const dates = shell.dates ?? (summary && eventDates(summaryDays(summary)))

  const label = (
    <Stack sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
      <Typography
        noWrap
        sx={{ fontWeight: compact ? 800 : 700, fontSize: compact ? 18 : 14, lineHeight: 1.3 }}
      >
        {event.name}
      </Typography>
      {dates && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {dates}
        </Typography>
      )}
    </Stack>
  )
  return (
    <>
      <ButtonBase
        aria-label="Mes événements"
        aria-haspopup="menu"
        onClick={(e) => setAnchor(e.currentTarget as HTMLElement)}
        sx={{
          flex: 1,
          minWidth: 0,
          gap: 1,
          justifyContent: 'flex-start',
          borderRadius: 3,
          p: compact ? 0 : 1,
          bgcolor: compact ? undefined : 'background.default',
        }}
      >
        {label}
        <ExpandMore fontSize="small" color="action" />
      </ButtonBase>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        <MenuItem component="a" href="/" onClick={() => setAnchor(null)}>
          Tous mes événements
        </MenuItem>
        <Divider />
        {sameRole.map((other) => (
          <MenuItem
            key={other.slug}
            component="a"
            href={`/events/${other.slug}${current.path === '/' ? '' : current.path}`}
            selected={other.slug === event.slug}
            onClick={() => setAnchor(null)}
          >
            <ListItemText>{other.name}</ListItemText>
            {other.slug === event.slug && <Check fontSize="small" sx={{ ml: 2 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

function SideNav() {
  const { event } = useEvent()
  const { sections, current, href } = useNavigation()
  const { badges } = useContext(ShellContext)
  const item = (section: Section) => {
    const count = (section.badge && badges[section.badge]) || 0
    const selected = section === current
    return (
      <ListItemButton
        key={section.path}
        component="a"
        href={href(section)}
        selected={selected}
        aria-current={selected ? 'page' : undefined}
        sx={{
          borderRadius: 2.5,
          py: 1.1,
          color: selected ? 'primary.main' : 'text.primary',
          '&.Mui-selected': { bgcolor: 'primary.light' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
          <SectionIcon section={section} />
        </ListItemIcon>
        <ListItemText
          primary={section.label}
          slotProps={{ primary: { sx: { fontWeight: selected ? 600 : 500, fontSize: 14 } } }}
        />
        {section.badge && count > 0 && (
          <Badge
            badgeContent={count}
            color={badgeColors[section.badge]}
            max={99}
            sx={{ mr: 1.5 }}
          />
        )}
      </ListItemButton>
    )
  }
  return (
    <Box
      component="nav"
      aria-label="Navigation principale"
      sx={{
        display: { xs: 'none', md: 'flex' },
        displayPrint: 'none',
        flexDirection: 'column',
        position: 'fixed',
        inset: '0 auto 0 0',
        width: DRAWER_WIDTH,
        p: 1.75,
        gap: 0.5,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', mb: 2 }}>
        <EventSwitcher />
      </Box>
      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {sections.filter((section) => !section.apart).map(item)}
      </List>
      <Box sx={{ flex: 1 }} />
      <List disablePadding>{sections.filter((section) => section.apart).map(item)}</List>
      <Divider sx={{ mt: 1 }} />
      <AccountMenu subtitle={event.role === 'organizer' ? 'Organisateur' : 'Bénévole'} />
    </Box>
  )
}

function TopBar() {
  return (
    <Stack
      component="header"
      direction="row"
      sx={{
        display: { xs: 'flex', md: 'none' },
        displayPrint: 'none',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <EventSwitcher compact />
      <AccountMenu />
    </Stack>
  )
}

function TabBar() {
  const { sections, current, href } = useNavigation()
  const { badges } = useContext(ShellContext)
  const [more, setMore] = useState(false)
  const tabs = sections.filter((section) => section.tab)
  const others = sections.filter((section) => !section.tab)
  const value = current.tab ? current.path : 'more'
  return (
    <>
      <BottomNavigation
        component="nav"
        aria-label="Navigation principale"
        showLabels
        value={value}
        sx={{
          display: { xs: 'flex', md: 'none' },
          displayPrint: 'none',
          position: 'fixed',
          inset: 'auto 0 0 0',
          zIndex: 'appBar',
          height: `calc(${TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`,
          pb: 'env(safe-area-inset-bottom)',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {tabs.map((section) => {
          const count = (section.badge && badges[section.badge]) || 0
          return (
            <BottomNavigationAction
              key={section.path}
              value={section.path}
              label={section.tab}
              component="a"
              href={href(section)}
              aria-current={section === current ? 'page' : undefined}
              icon={
                <Badge
                  badgeContent={count}
                  color={section.badge ? badgeColors[section.badge] : 'primary'}
                  max={99}
                >
                  <SectionIcon section={section} />
                </Badge>
              }
              sx={{ minWidth: 0 }}
            />
          )
        })}
        {others.length > 0 && (
          <BottomNavigationAction
            value="more"
            label="Plus"
            icon={<MoreHoriz />}
            onClick={() => setMore(true)}
            sx={{ minWidth: 0 }}
          />
        )}
      </BottomNavigation>
      <Drawer anchor="bottom" open={more} onClose={() => setMore(false)}>
        <List aria-label="Autres sections" sx={{ pb: 'env(safe-area-inset-bottom)' }}>
          {others.map((section) => (
            <ListItemButton
              key={section.path}
              component="a"
              href={href(section)}
              selected={section === current}
              onClick={() => setMore(false)}
            >
              <ListItemIcon>
                <SectionIcon section={section} />
              </ListItemIcon>
              <ListItemText primary={section.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  )
}

/**
 * Navigation of an event space, by the user's role: a fixed side menu on a computer, a top bar
 * and a tab bar on a phone. `badges` are the counts next to the sections.
 */
export function EventShell({ badges, dates, children }: Shell & { children: ComponentChildren }) {
  return (
    <ShellContext.Provider value={{ badges, dates }}>
      <SideNav />
      <TopBar />
      <Box
        component="main"
        sx={{
          ml: { md: `${DRAWER_WIDTH}px` },
          pb: { xs: `calc(${TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`, md: 0 },
          '@media print': { ml: 0, pb: 0 },
        }}
      >
        {children}
      </Box>
      <TabBar />
    </ShellContext.Provider>
  )
}
